import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations, useCurrentUser } from '@/hooks';
import { buildFacilityOptions } from '@/config/formOptions';
import { EMPTY_LIST } from '@/lib/utils';
import {
  FulfillForm,
  FulfillSuccessModal,
  FacilityField,
  RetireCreditsField,
  DependentDropdownField,
  ReferenceIdField,
  DateOfDispatchField,
  VesselNameField,
  ShipmentQuantityField,
  ShipmentQuantityUnitField,
  buildBatchesFromValues,
} from '@/components/common';
import { FulfillTerminalField } from '../components/FulfillTerminalField';
import {
  SHIPMENT_OPTION_CLEARS,
  buildShipmentOptionOptions,
  buildShipmentUnitOptions,
  resolveAssignedQuantity,
  resolveAssignedQuantityUnit,
} from '../constants/fulfillOrder.constants';
import { fulfillOrderValidation, buildFulfillQuantityRules } from '../fulfillOrderValidation';
import { useGetOrderForFulfillQuery, useDispatchOrderMutation } from '../api/ordersApi';
import { FULFILL_ORDER_CONFIG } from '../constants/fulfillOrderConfig';

// Outbounds ▸ Orders ▸ Fulfill.
export const FulfillOrderPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, orderId } = useParams();
  const user = useCurrentUser();
  const [isSuccessOpen, setSuccessOpen] = useState(false);
  // Filled by the fulfil's response; Generate declaration opens these.
  const [createdAllocationIds, setCreatedAllocationIds] = useState([]);

  const { data: order, isLoading, isError } = useGetOrderForFulfillQuery({ id: orderId });
  const [dispatchOrder, { isLoading: isDispatching }] = useDispatchOrderMutation();

  const goToOrder = useCallback(() => {
    navigate(ROUTES.orderDetail(module, orderId));
  }, [navigate, module, orderId]);

  const goToOrders = useCallback(() => {
    navigate(ROUTES.orders(module));
  }, [navigate, module]);

  const goToGenerateDeclaration = useCallback(() => {
    navigate(ROUTES.generateDeclarationFor(module, createdAllocationIds));
  }, [navigate, module, createdAllocationIds]);

  const handleSubmit = useCallback(
    async (values) => {
      // The Allocate credit section already shows the mixed-units Alert inline.
      if (values.hasMixedUnitBatches) return;
      const assigned = resolveAssignedQuantity(values);
      const assignedQuantityUnit = resolveAssignedQuantityUnit(values, order);
      // The picks and their typed amounts are form fields; the payload wants them as one list.
      const batches = buildBatchesFromValues(values);
      try {
        const allocationIds = await dispatchOrder({
          module,
          id: orderId,
          values: { ...values, assignedQuantity: assigned, assignedQuantityUnit, batches },
        }).unwrap();
        setCreatedAllocationIds(allocationIds);
        setSuccessOpen(true);
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [dispatchOrder, module, orderId, order],
  );

  const facilityOptions = useMemo(
    () =>
      buildFacilityOptions({
        sites: user?.sites ?? EMPTY_LIST,
        backToBackAccounts: user?.backToBackAccounts ?? EMPTY_LIST,
      }),
    [user],
  );

  // <Form> seeds initialValues once, on mount, so it waits for the order.
  const initialValues = useMemo(() => order?.values, [order]);

  const sites = user?.sites ?? EMPTY_LIST;

  // The fields each section shows; the config says which sections exist and when.
  const slots = useMemo(
    () => ({
      allocations: (
        <>
          <FacilityField options={facilityOptions} label={t('FIELD_FACILITY_NAME')} disabled />
          <FulfillTerminalField sites={sites} disabled={Boolean(initialValues?.terminalId)} />
          <RetireCreditsField />
        </>
      ),
      shipmentOption: (
        <DependentDropdownField
          name="shipmentOption"
          label={t('FIELD_SHIPMENT_OPTION')}
          placeholder={t('FORM_SELECT_SHIPMENT_OPTION')}
          emptyMessage={t('COMMON_NO_OPTIONS')}
          options={buildShipmentOptionOptions(t)}
          clears={SHIPMENT_OPTION_CLEARS}
        />
      ),
      createNewShipment: (
        <>
          <ReferenceIdField
            label={t('FIELD_SHIPMENT_ID')}
            prefixLabel={t('SHIPMENT_FORM_SHIPMENT_ID_PREFIX')}
            numberLabel={t('SHIPMENT_FORM_SHIPMENT_ID_NUMBER')}
            // The server generates one when this is left blank.
            required={false}
            rules={fulfillOrderValidation.idNumber}
          />
          <ShipmentQuantityField
            capsByUnit={order?.remainingByUnit}
            buildRules={buildFulfillQuantityRules}
          />
          <ShipmentQuantityUnitField
            options={buildShipmentUnitOptions(t, order?.workingUnit, order?.declarationUnit)}
          />
          <DateOfDispatchField />
          <VesselNameField />
        </>
      ),
    }),
    [t, order, facilityOptions, sites, initialValues],
  );

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !order) {
    return (
      <EmptyState
        title={t('ORDERS_FULFILL_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToOrders}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <FulfillForm
        config={FULFILL_ORDER_CONFIG}
        module={module}
        record={order}
        slots={slots}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={goToOrder}
        isSubmitting={isDispatching}
      />

      <FulfillSuccessModal
        open={isSuccessOpen}
        onBackToOutbounds={goToOrders}
        onGenerateDeclaration={goToGenerateDeclaration}
      />
    </>
  );
};
